<template>
    <div
        :class="[classes, $attrs.class]"
        :style="$attrs.style"
    >
        <span
            v-if="$slots.prefix"
            :class="element('affix', modifier('start'))"
            aria-hidden="true"
        >
            <slot name="prefix" />
        </span>

        <input
            ref="control"
            v-model="model"
            :class="element('control')"
            :type="type"
            :name="name"
            :placeholder="placeholder"
            :readonly="readonly || undefined"
            v-bind="{ ...controlAttrs, ...fallthroughAttrs }"
        >

        <button
            v-if="showClear"
            type="button"
            :class="element('clear')"
            :aria-label="clearLabel"
            @click="clear"
        >
            <slot name="clear">
                &times;
            </slot>
        </button>

        <span
            v-if="$slots.suffix"
            :class="element('affix', modifier('end'))"
        >
            <slot name="suffix" />
        </span>
    </div>
</template>

<script setup lang="ts">
import type { KInputProps } from './types'
import useBem from '@yeeroen/use-bem'
import { computed, nextTick, useAttrs, useTemplateRef } from 'vue'

import { useFieldControl } from '../field/use-field-control'

defineOptions({ name: 'KInput', inheritAttrs: false })

const props = withDefaults(defineProps<KInputProps>(), {
    type: 'text',
    clearable: false,
    clearLabel: 'Clear',
    fullWidth: true,
    readonly: false,
    // Left undefined on purpose: `useFieldControl` falls back to the KField context with
    // `??`, and a `false` default would win over an enclosing disabled field.
    disabled: undefined,
    required: undefined,
    invalid: undefined,
    size: undefined,
})

defineSlots<{
    prefix?: () => unknown
    suffix?: () => unknown
    clear?: () => unknown
}>()

const model = defineModel<string | number>({ default: '' })

const attrs = useAttrs()
const { block, element, modifier } = useBem('k-input')

// `inheritAttrs: false` sweeps `class` and `style` into `$attrs` as well, but a caller
// adding a class means the box, not the text node inside it.
const fallthroughAttrs = computed(() => {
    const { class: _class, style: _style, ...rest } = attrs

    return rest
})
const { controlAttrs, invalid, disabled, size } = useFieldControl(props)
const control = useTemplateRef<HTMLInputElement>('control')

const showClear = computed(() => props.clearable
    && model.value !== ''
    && model.value !== undefined
    && !disabled.value
    && !props.readonly)

const classes = computed(() => block(
    modifier(size.value),
    modifier('invalid', invalid.value),
    modifier('disabled', disabled.value),
    modifier('readonly', props.readonly),
    modifier('full-width', props.fullWidth),
))

async function clear() {
    model.value = ''
    // The clear button unmounts itself on the same tick; focus would otherwise land on
    // the body.
    await nextTick()
    control.value?.focus()
}
</script>

<style lang="scss">
@use './k-input.scss';
</style>
